import {
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  TextField,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { MdVisibility, MdVisibilityOff } from "react-icons/md";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext } from "./AuthContext";
import Loader from "../../component/Loader";
import PopUpAlert from "../../component/PopUpAlert";
import loginImg from "../../assets/images/locksmith.png";
import { IoMdCheckmarkCircleOutline } from "react-icons/io";
import { isInvitePasswordValid, parseInviteEmail } from "../../util/inviteActivate";

const InviteActivate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { memberSignIn, isLoading, message } = useContext(AuthContext);
  const [username, setUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [tempPassword, setTempPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showTempPassword, setShowTempPassword] = useState(false);
  const [passwordValidate, setPasswordValidate] = useState({
    length: false,
    specialSymbal: false,
    allowedChars: false,
    number: false,
    upperLetter: false,
    lowerLetter: false,
  });
  const [errors, setErrors] = useState({
    username: false,
    newPassword: false,
    tempPassword: false,
    isNewPasswordEmpty: false,
  });

  useEffect(() => {
    document.title = "Activate";
    const emailFromLink = parseInviteEmail(searchParams.toString());
    if (emailFromLink) {
      setUsername(emailFromLink);
    }
  }, [searchParams]);

  const handleUserName = (e) => {
    setErrors({});
    setUsername(e.target.value);
  };

  const handleNewPassword = (event) => {
    setErrors({});
    const { value } = event.target;
    const uppercasePattern = /[A-Z]/;
    const lowercasePattern = /[a-z]/;
    const numberPattern = /[0-9]/;
    const specialCharPattern = /[!@$*]/;
    const allowedCharsPattern = /^[A-Za-z0-9!@$*]+$/;
    const template = {};
    if (uppercasePattern.test(value)) {
      template.upperLetter = true;
    }
    if (lowercasePattern.test(value)) {
      template.lowerLetter = true;
    }
    if (specialCharPattern.test(value)) {
      template.specialSymbal = true;
    }
    if (allowedCharsPattern.test(value)) {
      template.allowedChars = true;
    }
    if (numberPattern.test(value)) {
      template.number = true;
    }
    if (value?.length >= 8 && value?.length <= 20) {
      template.length = true;
    }
    setPasswordValidate(template);
    setNewPassword(value);
  };

  const handleTempPassword = (e) => {
    setErrors({});
    setTempPassword(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errorFields = {};
    if (!username.trim()) {
      errorFields.username = true;
    }
    if (!newPassword.trim()) {
      errorFields.isNewPasswordEmpty = true;
    }
    if (!isInvitePasswordValid(passwordValidate)) {
      errorFields.newPassword = true;
    }
    if (!tempPassword.trim()) {
      errorFields.tempPassword = true;
    }
    setErrors(errorFields);
    if (errorFields.username || errorFields.tempPassword || errorFields.isNewPasswordEmpty || errorFields.newPassword) {
      return;
    }
    const loginDetails = {
      email: username.trim(),
      tmpPassword: tempPassword.trim(),
      password: newPassword.trim(),
    };
    const res = await memberSignIn(loginDetails);
    if (res?.Data) {
      navigate("/");
    }
  };

  return (
    <div className="login_container">
      {isLoading && <Loader />}
      <PopUpAlert type={message.type} message={message.content} />
      <form className="login_form" onSubmit={handleSubmit}>
        <div className="login_header">
          <img src={loginImg} alt="" />
          <h2>Activate your account</h2>
          <p>Paste the temporary password from your email, then choose a new password.</p>
        </div>
        <p className="invite-latest-note">
          Use this latest email. If an admin resent your invite, older temporary passwords no longer work.
        </p>
        <div className="login_input_fields">
          <TextField
            error={errors.username}
            helperText={errors.username && "Email is required"}
            className="login-text-input"
            fullWidth
            variant="outlined"
            label="Email"
            value={username}
            onChange={handleUserName}
            inputProps={{ "data-testid": "invite-email" }}
          />
          <FormControl
            fullWidth
            className="login-text-input"
            error={errors.tempPassword}
            variant="outlined"
          >
            <InputLabel htmlFor="invite-temp-password">Temporary password</InputLabel>
            <OutlinedInput
              id="invite-temp-password"
              type={showTempPassword ? "text" : "password"}
              value={tempPassword}
              onChange={handleTempPassword}
              autoComplete="off"
              inputProps={{ "data-testid": "invite-temp-password" }}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle temporary password visibility"
                    onClick={() => setShowTempPassword((show) => !show)}
                    edge="end"
                  >
                    {showTempPassword ? <MdVisibilityOff /> : <MdVisibility />}
                  </IconButton>
                </InputAdornment>
              }
              label="Temporary password"
            />
            {errors.tempPassword && (
              <FormHelperText sx={{ color: "red" }}>
                Temporary password is required
              </FormHelperText>
            )}
          </FormControl>
          <FormControl
            fullWidth
            className="login-text-input"
            error={errors.newPassword || errors.isNewPasswordEmpty}
            variant="outlined"
          >
            <InputLabel htmlFor="invite-new-password">New password</InputLabel>
              <OutlinedInput
                id="invite-new-password"
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={handleNewPassword}
                inputProps={{ "data-testid": "invite-new-password" }}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle new password visibility"
                    onClick={() => setShowNewPassword((show) => !show)}
                    edge="end"
                  >
                    {showNewPassword ? <MdVisibilityOff /> : <MdVisibility />}
                  </IconButton>
                </InputAdornment>
              }
              label="New password"
            />
            {errors.isNewPasswordEmpty && (
              <FormHelperText sx={{ color: "red" }}>
                New password is required
              </FormHelperText>
            )}
          </FormControl>
          <div className="password_validate_message_box">
            <p className={passwordValidate.upperLetter ? "password_validate_message active" : "password_validate_message"}>
              <IoMdCheckmarkCircleOutline className="password_validate_log" />
              Require at least 1 Uppercase.
            </p>
            <p className={passwordValidate.lowerLetter ? "password_validate_message active" : "password_validate_message"}>
              <IoMdCheckmarkCircleOutline className="password_validate_log" />
              Require at least 1 lowercase.
            </p>
            <p className={passwordValidate.specialSymbal ? "password_validate_message active" : "password_validate_message"}>
              <IoMdCheckmarkCircleOutline className="password_validate_log" />
              Must include exactly one of: ! @ $ * (no other special characters).
            </p>
            <p className={passwordValidate.allowedChars ? "password_validate_message active" : "password_validate_message"}>
              <IoMdCheckmarkCircleOutline className="password_validate_log" />
              No other characters allowed.
            </p>
            <p className={passwordValidate.number ? "password_validate_message active" : "password_validate_message"}>
              <IoMdCheckmarkCircleOutline className="password_validate_log" />
              Require at least 1 Number.
            </p>
            <p className={passwordValidate.length ? "password_validate_message active" : "password_validate_message"}>
              <IoMdCheckmarkCircleOutline className="password_validate_log" />
              Minimum 8 and Maximum 20 characters.
            </p>
          </div>
        </div>
        <div className="login_btn">
          <Button type="submit" fullWidth variant="contained">
            Activate
          </Button>
        </div>
        <div className="login_privacy_policy">
          <p>
            By activating, you agree to the Sportmingle{" "}
            <Link to={"https://sportmingle.com/terms_and_conditions.html"}>Term of Services</Link> and{" "}
            <Link to={"https://sportmingle.com/privacy_policy.html"}>Privacy Policy</Link>
          </p>
        </div>
        <div className="login_create_account">
          <p>
            Already activated? <Link to={"/login"}>Log in</Link>
          </p>
        </div>
      </form>
    </div>
  );
};

export default InviteActivate;
